import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ArrowLeft from '../../Assets/Arrow left.svg';
import ImageAdd from '../../Assets/Add image.svg';
import { CropImageContext } from '../../Context/CropImageContext';
import CropImage from '../../Components/CropImage/CropImage';
import { formatDate, getMediaUrl, loadImage } from '../../lib/utils/utils';
import { v4 as uuidv4 } from 'uuid';
import imageCompression from 'browser-image-compression';
import { createCollection } from '../../lib/utils/apiCalls';
import { showToast } from '../../Components/Toaster/Toaster';
import { useDispatch } from 'react-redux';
import { setModal } from '../../Features/collectionsSlice';
import Modal from '../Collections/Components/Modals/Modal';
import RedDelete from "../../Assets/redDelete.svg";
import AddBlack from "../../Assets/PlusIcon/AddBlack.svg";
import { CollectionFormContext } from '../../Context/CollectionFormContext';
import LoadingTransparent from '../../Components/Loading/LoadingTransparent';

const formatLocalDate = (isoString) => {
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

function AddCollection() {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    const { selectedProducts, setSelectedProducts, date, setDate } = useContext(CollectionFormContext);

    const handleCancel = () => {
        navigate(`${location.pathname.split('/').slice(0, -2).join('/')}`);
    }

    const [loading, setLoading] = useState(false);
    const { setImageToCrop, croppedImage, setCroppedImage, isImageCropped, setIsImageCropped, modalOpen, setModalOpen, setAspectRatio } = useContext(CropImageContext);

    const defaultFormInputs = {
        name: '',
        description: '',
    }

    const defaultFormErrors = {
        name: '',
        description: '',
        image: '',
    }

    const [formInputs, setFormInputs] = useState(defaultFormInputs);
    const [formErrors, setFormErrors] = useState(defaultFormErrors);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormInputs({ ...formInputs, [name]: value });
    }

    const [image, setImage] = useState({});

    const handleImageSelection = async (e) => {
        const file = e.target.files[0];
        setImageToCrop(file);
        setAspectRatio(1 / 1);
        setModalOpen(true);
        e.target.value = null;
    }

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        setImageToCrop(file);
        setAspectRatio(1 / 1);
        setModalOpen(true);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const processFile = async (file) => {
        if(file) {
            const img = await loadImage(file);
            const selectedImage = {
                id: uuidv4(),
                url: img.src,
                file: file
            };
            setImage(selectedImage);
        }
        else {
            return;
        }
    }

    useEffect(() => {
        if(isImageCropped) {
            processFile(croppedImage);
            setIsImageCropped(false);
            setCroppedImage({});
            setModalOpen(false);
        }

        // eslint-disable-next-line
    }, [isImageCropped]);

    const [modalVisible, setModalVisible] = useState(false);

    useEffect(() => {
        if(modalOpen) {
            setModalVisible(true);
        } else {
            setModalVisible(false);
        }
    }, [modalOpen]);

    const [hoverOnImage, setHoverOnImage] = useState(false);
    const [buttonsVisible, setButtonsVisible] = useState(false);

    useEffect(() => {
        if(hoverOnImage) {
            setButtonsVisible(true);
        } else {
            setButtonsVisible(false);
        }
    }, [hoverOnImage]);

    const handleDeleteImage = () => {
        setImage({});
    }

    const handleReplaceImage = async (e) => {
        setHoverOnImage(false);
        const file = e.target.files[0];
        setImageToCrop(file);
        setModalOpen(true);
        e.target.value = null;
    }

    const handleSaveCollection = async () => {
        const validationErrors = {};

        if(!formInputs.name.trim()) {
            validationErrors.name = 'Required field';
        }

        if(Object.keys(image).length === 0) {
            validationErrors.image = 'Image is required';
        }

        if(formInputs.description.length > 255) {
            validationErrors.description = 'Max 255 characters';
        }

        if(Object.keys(validationErrors).length === 0) {
            setFormErrors(defaultFormErrors);
            setLoading(true);

            try {
                let url;

                if(image.file.size > 1048576) {
                    try {
                        let compressedBlob = await imageCompression(image.file, {
                            maxSizeMB: 1,
                            maxWidthOrHeight: 1920,
                            useWebWorker: true
                        });
    
                        let compressedFile = new File([compressedBlob], image.file.name, { type: image.file.type, lastModified: Date.now() });
                    
                        if (compressedFile) {
                            url = await getMediaUrl(compressedFile);
                        }
                        else {
                            url = await getMediaUrl(image.file);
                        }
                    }
                    catch(err) {
                        url = await getMediaUrl(image.file);
                    }
                }
                else {
                    url = await getMediaUrl(image.file);
                }

                const callData = {
                    CategoryId: params.categoryId,
                    CollectionName: formInputs.name,
                    CollectionDesc: formInputs.description,
                    CollectionImgUrl: [url],
                    isVisible: true
                }

                if(date.from && date.to) {
                    callData.startDuration = formatLocalDate(date.from);
                    callData.endDuration = formatLocalDate(date.to);
                }

                if(selectedProducts?.length > 0) {
                    callData.products = selectedProducts.map(product => product.product.id);
                }

                const response = await createCollection(callData);
                if(response?.status === 200) {
                    showToast('Collection created.', 'success');
                    setLoading(false);
                    handleCancel();
                }
                else {
                    showToast('Error creating collection.', 'error');
                    setLoading(false);
                }
            }
            catch(err) {
                console.log(err);
                setLoading(false);
            }
        }
        else {
            setFormErrors(validationErrors);
            setLoading(false);
            return;
        }
    }

    const handleAddProductClick = () => {
        dispatch(setModal('select-product'));
    }

    const handleAddDuration = () => {
        dispatch(setModal('select-duration'));
    }

    const handleRemoveProduct = (id) => {
        const filteredProducts = selectedProducts.filter(product => product.product.id !== id);
        setSelectedProducts(filteredProducts);
    }

    return (
        <div className="w-full h-full overflow-y-scroll pb-16 outline-none">
            <div className="select-none flex items-center gap-2 relative m-[20px_19px_24px_20px]">
                <div onClick={handleCancel} className="p-[10px] flex items-center justify-center cursor-pointer">
                    <img className='size-6 text-[#666666]' src={ArrowLeft} alt="" />
                </div>

                <h1 className="p-[6px] text-[#121212] font-bold leading-8 text-[24px]">Add Collection</h1>
            </div>

            <div className="flex flex-col gap-6 mt-4 mx-5 w-[70%]">
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-2 border border-[#EAEAEA] rounded-xl p-5">
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                                <label className='font-medium text-[18px] leading-6 text-[#121212] w-fit cursor-pointer select-none' htmlFor="name">Collection name</label>
                            
                                <p className='text-[14px] leading-5 text-[#DC2626] select-none'>{formErrors.name}</p>
                            </div>

                            <input 
                                className='outline-none px-3 py-4 rounded-[9px] bg-[#F5F5F5] border border-[#EAEAEA] placeholder:font-normal placeholder:text-[#666666] text-base font-medium text-[#121212] focus:border-primaryColor'
                                id='name' 
                                name='name' 
                                type="text" 
                                placeholder='Enter collection name' 
                                value={formInputs.name} 
                                onChange={handleChange} 
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                                <label className='font-medium text-[18px] leading-6 text-[#121212] w-fit cursor-pointer select-none' htmlFor="description">Description</label>

                                <p className='text-[14px] leading-5 text-[#DC2626] select-none'>{formErrors.description}</p>
                            </div>

                            <textarea 
                                name="description" 
                                id="description" 
                                className='outline-none px-3 py-4 rounded-[9px] bg-[#F5F5F5] border border-[#EAEAEA] h-44 focus:border-primaryColor' 
                                placeholder='Write a short description about this collection(Optional)' 
                                value={formInputs.description} 
                                onChange={handleChange} 
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-4 border border-[#EAEAEA] p-5 rounded-xl">
                        <div className="flex items-center justify-between">
                            <h1 className='font-semibold text-[18px] leading-[26px] select-none'>
                                Add Image <span className='text-base text-[#666666]'>&#40;1080px * 1080px&#41;</span>
                            </h1>

                            <p className='text-[14px] leading-5 text-[#DC2626] select-none'>{formErrors.image}</p>
                        </div>

                        <div 
                            onDrop={handleDrop} 
                            onDragOver={handleDragOver} 
                            onDragLeave={handleDragLeave} 
                            className="p-3 flex flex-wrap gap-3 rounded-xl bg-[#F5F5F5]"
                        >
                            {
                                Object.keys(image).length === 0 ? 
                                <label htmlFor='product-image' className="cursor-pointer border-dashed bg-[#E5E5E5] border-2 border-[#D1D5DB] size-[140px] rounded-lg flex flex-col items-center justify-center gap-2">
                                    <div className="w-12 h-12 flex items-center justify-center">
                                        <img src={ImageAdd} alt="" />
                                    </div>

                                    <p className='font-medium text-sm text-[#4B5563] w-[90%] text-center'>
                                        <span className='text-[#4F46E5]'>Click to Upload</span> or Drag or drop
                                    </p>
                                </label> : 
                                <div 
                                    onMouseEnter={() => setHoverOnImage(true)} 
                                    onMouseLeave={() => setHoverOnImage(false)} 
                                    className='relative size-[140px] rounded-lg overflow-hidden'
                                >
                                    <img className='size-full object-cover' src={image.url} alt="" />

                                    <div className={`${buttonsVisible ? 'opacity-100 visible' : 'opacity-0 invisible'} transition-all delay-100 ease-in-out w-full h-full bg-[#00000066] absolute top-0 left-0 items-center justify-center flex flex-col gap-2`}>
                                        <label htmlFor={image.id} className='select-none bg-[#FFFFFF] w-3/5 py-[6px] text-sm font-semibold rounded text-center cursor-pointer'>Replace</label>
                                        
                                        <button onClick={handleDeleteImage} className='select-none bg-[#FFFFFF] w-3/5 py-[6px] text-sm font-semibold rounded'>Delete</button>

                                        <input 
                                            className='hidden' 
                                            id={image.id} 
                                            type="file" 
                                            accept="image/*" 
                                            onChange={handleReplaceImage} 
                                        />
                                    </div>
                                </div>
                            }

                            <input 
                                onChange={handleImageSelection} 
                                className='hidden'
                                type="file" 
                                accept="image/*" 
                                name="product-image" 
                                id="product-image" 
                            />
                        </div>
                    </div>

                    <div className="border border-[#EAEAEA] bg-[#FFFFFF] p-5 rounded-xl flex flex-col gap-6">
                        <div className="flex items-center justify-between">
                            <h1 className='font-semibold text-[#121212] text-[18px] leading-6'>Products</h1>

                            <button onClick={handleAddProductClick} className='flex items-center gap-1 font-semibold text-base text-primaryColor'>
                                <div className="size-6 flex items-center justify-center">
                                    <img src={AddBlack} alt="" />
                                </div>

                                Add Product
                            </button>
                        </div>

                        {
                            selectedProducts?.length > 0 ?
                            <div className="border border-[#EAEAEA] rounded-lg overflow-hidden flex flex-col">
                                <div className="px-[10px] py-2 bg-[#F5F5F5]">
                                    <p className='text-base font-semibold text-[#121212] select-none'>{formInputs.name ? formInputs.name : 'Collection name'}</p>
                                </div>

                                <div className="flex flex-col w-full max-h-80 overflow-y-scroll shadow-[inset_0_-2px_8px_rgba(0,0,0,0.1)]">
                                    {selectedProducts?.map(product => {
                                        return (
                                            <div key={product.product.id} className='select-none border-t border-[#EAEAEA] px-[10px] py-2 flex items-center gap-1'>
                                                <div className="size-14 rounded-lg overflow-hidden">
                                                    <img className='size-full object-cover' src={product?.product?.productImgUrls[0]} alt="" />
                                                </div>

                                                <p className='w-[calc(100%-56px-44px)] font-medium text-[14px] leading-6 text-[#121212] whitespace-nowrap overflow-hidden text-ellipsis'>{product.product.productName}</p>

                                                <button onClick={() => handleRemoveProduct(product.product.id)} className="w-11 h-[42px] outline-none px-[5px] py-[6px]">
                                                    <div className="size-8 flex items-center justify-center">
                                                        <img src={RedDelete} alt="" />
                                                    </div>
                                                </button>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div> : 
                            <div className="flex flex-col items-center gap-2 text-[#666666] leading-7">
                                <h1 className='font-semibold text-[18px]'>No products are added yet</h1>

                                <p className='font-medium text-[16px] text-center'>Include all the products you wish to have listed in this collection.</p>
                            </div>
                        }
                    </div>

                    <div className="border border-[#EAEAEA] bg-[#FFFFFF] p-5 rounded-xl flex flex-col gap-6">
                        <div className="flex items-center justify-between">
                            <h1 className='font-semibold text-[#121212] text-[18px] leading-6'>Duration</h1>

                            <button onClick={handleAddDuration} className='flex items-center gap-1 font-semibold text-base text-primaryColor'>
                                <div className="size-6 flex items-center justify-center">
                                    <img src={AddBlack} alt="" />
                                </div>

                                Set Duration
                            </button>
                        </div>

                        {
                            (date.from && date.to) ? 
                            <div className="border border-[#EAEAEA] rounded-lg overflow-hidden flex flex-col">
                                <div className="px-[10px] py-2 bg-[#F5F5F5] flex items-center">
                                    <p className='text-base font-semibold text-[#121212] select-none w-[calc((100%-44px)/2)]'>Start Date</p>

                                    <p className='text-base font-semibold text-[#121212] select-none w-[calc((100%-44px)/2)]'>End Date</p>
                                
                                    <div className="w-11"></div>
                                </div>

                                <div className="flex items-center px-[10px] py-2 font-medium text-[14px] leading-6 text-[#121212]">
                                    <p className="w-[calc((100%-44px)/2)]">{formatDate(date.from)} &#40;UTC&#41;</p>

                                    <p className="w-[calc((100%-44px)/2)]">{formatDate(date.to)} &#40;UTC&#41;</p>

                                    <button onClick={() => { setDate({ from: null, to: null }) }} className="w-11 h-[42px] outline-none px-[5px] py-[6px]">
                                        <div className="size-8 flex items-center justify-center">
                                            <img src={RedDelete} alt="" />
                                        </div>
                                    </button>
                                </div>
                            </div> : 
                            <div className="flex flex-col items-center gap-2 text-[#666666] leading-7">
                                <h1 className='font-semibold text-[18px]'>No duration are added yet</h1>

                                <p className='font-medium text-[16px] text-center'>This feature allows you to define the time period during which this collection will be visible within this category in the store.</p>
                            </div>
                        }
                    </div>
                </div>

                <div className="ml-auto flex items-center gap-3">
                    <button onClick={handleCancel} className='px-4 py-2 rounded bg-[#FFFFFF] font-semibold text-base text-[#808080]'>Cancel</button>

                    <button onClick={handleSaveCollection} className='px-4 py-2 rounded bg-primaryColor hover:bg-primaryAccent font-semibold text-base text-buttonText'>Save Collection</button>
                </div>
            </div>

            {
                modalOpen && 
                <div className={`${modalVisible ? 'opacity-100 visible bg-[#00000080]' : 'opacity-0 invisible'} transition-all duration-100 ease-in-out fixed z-50 w-full h-full top-0 left-0 flex justify-center items-center`}>
                    <CropImage />
                </div>
            }

            <Modal />

            {loading && <LoadingTransparent />}
        </div>
    );
}

export default AddCollection;
